import axios, { InternalAxiosRequestConfig, AxiosInstance, AxiosError } from 'axios';
import pRetry from 'p-retry';
import { getConfig } from '../helpers';
import queryStringManager from './query-string-manager';
import SPGVariables from './spg-variables';
import Store from '../redux/store';
import { ErrorManager } from './error-manager';
import { toggleLogInStatus, clearUserProfile } from '../redux/user/user.actions';
import * as HeapUtils from './heap';
import { clearCollections } from '../redux/collections/collections.actions';

const spgVariables = SPGVariables();

const securityConfig = getConfig<any>('utilities.security');
const pRetryOptions = getConfig<any>('utilities.pRetryOptions');
const urls = getConfig<any>('urls.mi.security');

const { suppressAuthorizationHeader }: { suppressAuthorizationHeader: string[] } = securityConfig;

let miCredentials: AnonymousObject;
let tokenRefreshTimer: ReturnType<typeof setTimeout>;
let currentAccessToken: AccessToken | undefined;
let pendingAuthentication: Promise<AccessToken> | undefined;

// initialize axios instance to handle requests to security service
const axiosSecurityClient: AxiosInstance = axios.create({
    baseURL: spgVariables.APIBASE,
    timeout: securityConfig.requestTimeout,
    headers: {
        'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8'
    }
});

const sleep = (milliseconds: number) => {
    return new Promise<void>(resolve => setTimeout(resolve, milliseconds));
};

// Cross-tab logout sync via BroadcastChannel
const logoutSyncChannel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('logout_sync') : null;

// removes the token from the server-side store (DynamoDB) so it can no longer be used
const revokeAccessToken = (accessToken: string) => (
    axiosSecurityClient.get(urls.signOut, {
        params: { domains: window.location.hostname },
        headers: { Authorization: `Bearer ${accessToken}` }
    })
        .catch(() => {
            console.error('Revoke token error');
        })
);

const signOut = async (clearCookies = true, broadcast = true) => {
    clearTimeout(tokenRefreshTimer);
    const tokenToRevoke = currentAccessToken?.access_token;
    currentAccessToken = undefined;

    if (clearCookies && tokenToRevoke) {
        await revokeAccessToken(tokenToRevoke);
    }
    localStorage.removeItem('state');
    localStorage.removeItem('stateUser');
    // Clear Heap user data
    HeapUtils.default.clearUserData();

    if (clearCookies && broadcast) {
        logoutSyncChannel?.postMessage('logout');
    }

    if (clearCookies && process.env.NODE_ENV === 'production') {
        const batchClearTokenCookies = axiosSecurityClient.post(
            urls.batchClearTokenCookies,
            queryStringManager.stringify({
                domains: [window.location.hostname],
                refreshToken: true
            })
        );
        const clearAllTokenCookies = axiosSecurityClient.post(
            urls.clearAllTokenCookies,
            queryStringManager.stringify({
                path: '/',
                domain: window.location.hostname
            })
        );
        await Promise.all([batchClearTokenCookies, clearAllTokenCookies])
            .catch((error: AxiosError) => {
                console.error('Clear token cookies error');
                // ErrorManager.apiError(error, { msg: 'Sign out process error.' });
            })
            .finally(() => {
                Store.dispatch(clearUserProfile());
                Store.dispatch(clearCollections());
            });
    } else {
        Store.dispatch(clearUserProfile());
        Store.dispatch(clearCollections());
    }
};

const getAccessToken = (): AccessToken | undefined => currentAccessToken;

// logout user from all browser tabs if user logged out from one tab
logoutSyncChannel?.addEventListener('message', () => {
    signOut(false, false);
});

const scheduleTokenRefresh = () => {
    // first, cancel any pending refresh
    clearTimeout(tokenRefreshTimer);
    // and determine the interval until we should refresh
    const accessToken = getAccessToken();
    if (accessToken) {
        const timeout = Math.max(accessToken.expires_in - new Date().getTime(), 0);
        if (timeout) {
            // set a callback
            tokenRefreshTimer = setTimeout(() => {
                // authenticate to get new tokens
                // eslint-disable-next-line @typescript-eslint/no-use-before-define
                pRetry(() => authenticate(false), pRetryOptions).catch((error: AxiosError) => {
                    ErrorManager.apiError(error, { msg: 'Authentication Error!' });
                    console.error('2. Security.tsx ', error);
                    signOut();
                });
            }, timeout);
        }
    }
};

// keep token info in memory only
const storeAccessToken = (accessToken: AccessToken) => {
    const expires = new Date().getTime() + accessToken.expires_in * 1000 * 0.75;
    currentAccessToken = { ...accessToken, expires_in: expires };
    scheduleTokenRefresh();
};

const getSnloAuthTokenInfo = async (): Promise<AccessToken | null> => {
    try {
        const response = await axiosSecurityClient.get<AccessToken>(
            urls.getSnloAuthTokenInfo,
            { withCredentials: true }
        );
        return response.data ?? null;
    } catch {
        return null;
    }
};
// ─────────────────────────────────────────────────────────────────────────────

// authenticate via security service
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const authenticate = (signoutOnFail = true): Promise<AccessToken> => {
    if (pendingAuthentication) {
        return pendingAuthentication;
    }
    pendingAuthentication = performAuthenticate(signoutOnFail).finally(() => {
        pendingAuthentication = undefined;
    });
    return pendingAuthentication;
};

const performAuthenticate = async (signoutOnFail = true): Promise<AccessToken> => {
    // ─── NEW: Reuse existing valid token if available ─────────────────────────
    const snloTokenInfo = await getSnloAuthTokenInfo();
    const expiryThresholdMs = securityConfig.tokenRefreshThresholdMs ?? 15 * 60 * 1000; // 15 min fallback

    if (
        snloTokenInfo &&
        snloTokenInfo.access_token &&
        snloTokenInfo.expires_in > 0 &&
        snloTokenInfo.expires_in * 1000 > expiryThresholdMs
    ) {
        storeAccessToken(snloTokenInfo);
        Store.dispatch(toggleLogInStatus(true));
        return snloTokenInfo;
    }
    // ─────────────────────────────────────────────────────────────────────────

    return new Promise<AccessToken>((resolve, reject) => {
        const requestBody = {
            scope: `${securityConfig.token.scopePart1}${securityConfig.token.scopePart2}`,
            grant_type: securityConfig.grantTypes.refreshToken
        };

        const environment = process.env as typeof process.env & {
            REACT_APP_RELEASELOCAL?: string;
            REACT_APP_MIUSERNAME?: string;
            REACT_APP_MIPASSWORD?: string;
        };

        if (process.env.NODE_ENV === 'development' || environment.REACT_APP_RELEASELOCAL) {
            if (environment.REACT_APP_MIUSERNAME && environment.REACT_APP_MIPASSWORD) {
                miCredentials = {
                    username: environment.REACT_APP_MIUSERNAME,
                    password: environment.REACT_APP_MIPASSWORD
                };
                requestBody.grant_type = securityConfig.grantTypes.password;
            }
        }

        if (!spgVariables.REFRESHTOKEN) {
            signOut(false).then(() => resolve(undefined as any)); // anonymous user
        } else {
            const body =
                requestBody.grant_type === securityConfig.grantTypes.refreshToken
                    ? { ...requestBody, refresh_token: spgVariables.REFRESHTOKEN }
                    : { ...requestBody, ...miCredentials };

            axiosSecurityClient
                .post<AccessToken>(urls.token, queryStringManager.stringify(body))
                .then((response: { data: AccessToken }) => {
                    storeAccessToken(response.data);
                    Store.dispatch(toggleLogInStatus(true));
                    axiosSecurityClient
                        .post(
                            urls.setTokenCookies,
                            JSON.stringify({
                                path: '/',
                                domain: window.location.hostname.substr(window.location.hostname.indexOf('.'))
                            }),
                            {
                                headers: { Authorization: `Bearer ${response.data.access_token}`, 'Content-Type': 'application/json' }
                            }
                        )
                        .catch((error: AxiosError) => {
                            ErrorManager.apiError(error, { msg: 'Authentication Error!' });
                            console.log('3. Security.tsx ', error);
                        });
                    resolve(response.data);
                })
                .catch((error: AxiosError) => {
                    const parameters = {
                        ServiceName: 'SecurityService',
                        RequestUri: urls.token,
                        error,
                        msg: 'Authentication Error!'
                    };
                    // ErrorManager.apiError(error, parameters);
                    console.log('4. Security.tsx ', error);

                    if (signoutOnFail) {
                        signOut().finally(() => reject());
                    } else {
                        reject(error);
                    }
                });
        }
    });
};

const setOAuthRequestInterceptor = (instance: AxiosInstance) => {
    let tokenRefreshInProgress = false;
    instance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
        return new Promise(resolve => {
            const url = config.url || '';

            // we have some urls that dont need a token
            const supressToken =
                url.length &&
                suppressAuthorizationHeader.some(regexStr => {
                    const regex = RegExp(regexStr, 'i');
                    return regex.test(url);
                });

            if (supressToken) {
                resolve(config);
                return;
            }
            if (tokenRefreshInProgress) {
                // Token refresh in progress
                pRetry(
                    () => {
                        return new Promise<void>((tokenAvailable, tokenNotAvailable) => {
                            if (tokenRefreshInProgress) {
                                tokenNotAvailable(new Error(url));
                            } else {
                                tokenAvailable(undefined as any);
                            }
                        });
                    },
                    {
                        retries: 10,
                        minTimeout: 100,
                        maxTimeout: 60 * 10 * 1000, // 10 mins
                        factor: 2.25,
                        onFailedAttempt: () => {
                            sleep(500); // snooze
                        }
                    }
                )
                    .then(() => {
                        // we have a new token
                        const token = getAccessToken();
                        if (token) {
                            config.headers.Authorization = `Bearer ${token.access_token}`;
                        }
                        resolve(config);
                    })
                    .catch(() => {
                        // still no token available, expect 401
                        console.warn(`no accesstoken available for ${config.url}`);
                        resolve(config);
                    });
            } else {
                const token = getAccessToken();
                if (token) {
                    const refreshThreshold = new Date().getTime() + 300000; // 5 minutes from now
                    if (refreshThreshold > token.expires_in) {
                        tokenRefreshInProgress = true;
                        // we got an old token, so lets get a new one before we proceed
                        pRetry(() => authenticate(false), pRetryOptions)
                            .then((response: AccessToken | undefined) => {
                                // got a new one, great! attach to header
                                if (response) {
                                    config.headers.Authorization = `Bearer ${response.access_token}`;
                                } else {
                                    config.headers.Authorization = `Bearer ${token.access_token}`;
                                }
                            })
                            .catch((error: AxiosError) => {
                                // security service unavailable, log and expect 401
                                ErrorManager.apiError(error, { msg: 'Authentication Error!' });
                                console.log('5. Security.tsx ', error);
                                // signout since we didnt pass that option when authenticating above
                                signOut();
                            })
                            .finally(() => {
                                // release waiting requests and allow to succeed or fail
                                tokenRefreshInProgress = false;
                                resolve(config);
                            });
                    } else {
                        // we have a token, attach to header
                        config.headers.Authorization = `Bearer ${token.access_token}`;
                        resolve(config);
                    }
                } else if (pendingAuthentication) {
                    pendingAuthentication
                        .then((newToken: AccessToken | undefined) => {
                            if (newToken) {
                                config.headers.Authorization = `Bearer ${newToken.access_token}`;
                            }
                            resolve(config);
                        })
                        .catch(() => resolve(config));
                } else {
                    resolve(config);
                }
            }
        });
    });
};

const security = {
    setOAuthRequestInterceptor,
    authenticate,
    signOut
};

export default security;
